import {
  getCustomPropertiesProvider,
  getPropertyProviders,
  getRepoFilterProviders,
  LanguageStaticFilterProvider,
  type PropertyDefinition,
  type ReposFilterAdditionalOptions,
  type ReposStaticProviderKey,
} from '@github-ui/repos-filter/providers'
import type {PickerRepository, PickerScope} from '@github-ui/repos-picker'
import {adjustProvidersToScope, useQueryDefinitions} from '@github-ui/repos-picker'
import {modes} from '@github-ui/repos-picker/modes'
import {useState} from 'react'
import {getTargetMode} from '../../../helpers/conditions'
import {buildQueryForAllProperties, queryToPropertyParameters} from '../../../helpers/custom-properties-query'
import type {
  Condition,
  ConditionParameters,
  ExpandedRepoTargetType,
  ExpandedTargetType,
  RepositoryIdConditionMetadata,
  RepositoryIdParameters,
  RepositoryPropertyParameters,
  SimpleRepository,
} from '@github-ui/repos-rules-types/rules-types'
import {ControlGroup} from '@github-ui/control-group'
import type {SuppliedFilterProvider} from '@github-ui/filter'
import type {PropertyValue, PropertyNamespace} from '@github-ui/custom-properties-types'
import {isPropertyValueArray} from '@github-ui/custom-properties-types/helpers'
import {CreatePropertyBanner} from '../../create-property-banner/CreatePropertyBanner'

interface Props {
  condition: Condition
  excludedTypes: ExpandedTargetType[]
  showPropertiesBanner?: boolean
  scope: PickerScope
  setCondition(newType: ExpandedTargetType, parameters?: ConditionParameters): void
  updateParameters(parameters: ConditionParameters): void
  additionalFilterProviders?: ReposStaticProviderKey[]
}

export function RulesetSelection({
  showPropertiesBanner,
  condition,
  excludedTypes,
  scope,
  setCondition,
  updateParameters,
  additionalFilterProviders,
}: Props) {
  const mode = getTargetMode(condition)
  const [knownRepos, setKnownRepos] = useState(() => {
    // metadata brings the repository objects used in parameters.repository_ids
    const metadata = condition.metadata as RepositoryIdConditionMetadata
    return getReposById(metadata?.repositories?.map(toPickerRepository) || [])
  })

  const {data: {definitions = [], namespaces = []} = {}} = useQueryDefinitions({
    scope,
    types: ['custom', 'external'],
  })
  const namespaceKeys = namespaces.map(ns => ns.namespace)

  const filterProviders = getFilterProviders(
    definitions,
    {
      additionalFilterProviders,
    },
    namespaces,
  )
  const providers = adjustProvidersToScope(filterProviders, scope)
  const selectedRepos = calculateSelected(mode, condition, knownRepos)
  const [matchingReposCount, setMatchingReposCount] = useState<number>()

  const builtModes = [
    {...modes.all, name: 'all_repos'},
    modes.buildMultiple({
      selected: selectedRepos,
      onSubmit: newRepos => {
        setKnownRepos({...knownRepos, ...getReposById(newRepos)})
        updateParameters({repository_ids: newRepos.map(r => r.nodeId)})
      },
      scope,
      name: 'repository_id',
    }),
    modes.buildFilter({
      query: calculateQuery(mode, condition),
      onSubmit: newQuery => {
        updateParameters(queryToPropertyParameters(newQuery, providers, namespaceKeys))
      },
      warnIfUnsupportedProvider: true,
      providers,
      scope,
      name: 'repository_property',
      matchingReposInitialCount: matchingReposCount,
    }),
    {name: 'repository_name', label: 'Repositories matching a name', description: 'Target repositories based on name'},
  ]
  const availableModes = builtModes.filter(({name}) => !excludedTypes.includes(name as ExpandedTargetType))

  // In this context scope is always organization or enterprise
  const sourceSlug = scope.type !== 'unbounded' ? scope.slug : 'Unknown'

  return (
    <>
      {showPropertiesBanner ? (
        <CreatePropertyBanner
          className="mb-3"
          sourceName={sourceSlug}
          selectedRepos={selectedRepos}
          condition={condition}
          onSuccess={(propertyName, appliedValue, count) => {
            setMatchingReposCount(count)
            setCondition('repository_property', getPropertyParameters(propertyName, appliedValue))
          }}
        />
      ) : null}
      <ControlGroup className="borderColor-default px-1">
        <ControlGroup.Selector
          selectedMode={mode}
          onModeChange={newMode => {
            if (showPropertiesBanner) {
              setMatchingReposCount(undefined)
            }
            setCondition(newMode as ExpandedRepoTargetType)
          }}
          modes={availableModes}
          title="Repository targeting criteria"
        />
      </ControlGroup>
    </>
  )
}

function calculateQuery(mode: ExpandedRepoTargetType, condition: Condition) {
  if (mode !== 'repository_property') {
    return ''
  }

  return buildQueryForAllProperties(condition.parameters as RepositoryPropertyParameters)
}

function calculateSelected(
  mode: ExpandedRepoTargetType,
  condition: Condition,
  knownRepos: Record<string, PickerRepository>,
): PickerRepository[] {
  if (mode !== 'repository_id') {
    return []
  }

  const parameters = condition.parameters as RepositoryIdParameters
  return parameters.repository_ids.map(nodeId => knownRepos[nodeId]).filter<PickerRepository>(repo => !!repo)
}

function toPickerRepository(repo: SimpleRepository): PickerRepository {
  return {
    ...repo,
    visibility: repo.public ? 'public' : 'private',
  }
}

function getReposById(repos: PickerRepository[]) {
  const reposById = {} as Record<string, PickerRepository>
  for (const repo of repos) {
    reposById[repo.nodeId] = repo
  }
  return reposById
}

function getFilterProviders(
  definitions: PropertyDefinition[] = [],
  options: ReposFilterAdditionalOptions = {},
  namespaces?: PropertyNamespace[],
): SuppliedFilterProvider[] {
  const baseProviders = ['visibility', 'fork'] as ReposStaticProviderKey[]

  const providers: SuppliedFilterProvider[] = [...getRepoFilterProviders(baseProviders, options)]

  if (namespaces && namespaces.length > 0) {
    providers.push(...getPropertyProviders(namespaces))
  } else if (definitions.length > 0) {
    providers.push(getCustomPropertiesProvider(definitions))
  }

  return [...providers, new LanguageStaticFilterProvider()]
}

function getPropertyParameters(propertyName: string, propertyValue: PropertyValue): RepositoryPropertyParameters {
  const values = isPropertyValueArray(propertyValue) ? propertyValue : [propertyValue]
  return {
    exclude: [],
    include: [{name: propertyName, source: 'custom', property_values: values}],
  }
}
